import { Button, ButtonProps } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { BaseRouter } from 'next/dist/shared/lib/router/router';
import { useRouter } from 'next/router';
import { forwardRef, ReactNode, Ref, useMemo } from 'react';

const styleBase = {
    borderRadius: 0,
};
/**
 * Helper function to determine if a nav link href is the active router pathname.
 *
 * If the pathname has multiple path segments (ex. /documents/essential),
 * the function checks to see if the pathname starts with the nav link href which
 * allows nest root nav links to display as active for a nested page.
 *
 * Example:
 * pathname: '/documents/essential'
 * navLinkHref: '/documents'
 * returns true
 */

export const isActiveNavItem = (
    pathname: BaseRouter['pathname'],
    navLinkHref?: NavButtonProps['href'],
    hasSubnav?: boolean
): boolean => {
    if (!navLinkHref) {
        return false;
    }
    const normalizePath = (path: string): string => {
        let p = path;
        if (!p.startsWith('/')) {
            p = `/${p}`;
        }
        if (!p.endsWith('/')) {
            p = `${p}/`;
        }
        return p;
    };
    const to = hasSubnav
        ? normalizePath(navLinkHref.split('/')[1])
        : normalizePath(navLinkHref);
    const from = normalizePath(pathname);
    if (to !== '/' && from.length > to.length) {
        return from.startsWith(to);
    } else {
        return to === from;
    }
};

export interface NavButtonProps extends ButtonProps {
    href?: string;
    hasSubnav?: boolean;
    altActiveHrefs?: string[];
    startIcon?: ReactNode;
    activeStateIcon?: ReactNode;
}
export const NavButton = forwardRef(
    (
        {
            children,
            href,
            sx,
            hasSubnav,
            altActiveHrefs,
            startIcon,
            activeStateIcon,
            ...otherProps
        }: NavButtonProps,
        ref: Ref<HTMLButtonElement>
    ) => {
        const { pathname } = useRouter();
        const isActive = useMemo(() => {
            if (isActiveNavItem(pathname, href, hasSubnav)) {
                return true;
            }
            if (altActiveHrefs && altActiveHrefs.length > 0) {
                return altActiveHrefs.some((altHref) =>
                    isActiveNavItem(pathname, altHref, hasSubnav)
                );
            }

            return false;
        }, [pathname, href, hasSubnav, altActiveHrefs]);

        return (
            <Button
                href={href}
                ref={ref}
                sx={sxAppend(styleBase, sx)}
                aria-current={isActive ? 'page' : undefined}
                startIcon={
                    isActive && activeStateIcon ? activeStateIcon : startIcon
                }
                {...otherProps}
            >
                {children}
            </Button>
        );
    }
);
NavButton.displayName = 'NavButton';

export default NavButton;
